import React, { useState, useEffect, useRef } from "react"
import { navigate } from "gatsby"
import Flink from '~/components/shared/flink'
import { useSearchContentQuery } from "~/hooks/useSearchContentQuery"
import useKeyboardEvent from '~/utils/keyboard-evt'
import useSearchHook from '~/utils/use-search'
import { useTheme } from '~/contexts/ThemeContext'
import useAnalytics from '~/utils/analytics'
import { imgSize, isStag, jnstyls, compact } from '~/utils/helpers'

import IconClose from '~/components/icons/close'
import IconSearch from '~/components/icons/search'
import SearchTiles from '~/components/testing/search-tiles'

import { aria_btn, solid_btn } from "~/styles/app.module.scss"
import * as tstyles from "~/styles/pdp/title.module.scss"
import * as styles from "~/styles/panels/search.module.scss"



const Search = () => {
  const { 
    isMobile, setFreeze, setSearch, analytics, setAnalytics, gender, setGender, setBread
  } = useTheme()
  const content = useSearchContentQuery()
  const { ga_search_click, elev_search, elev_select } = useAnalytics()

  let initgend = gender?.srGender?.slice(0,1) || null

  const [mobiHeight, setMobiHeight] = useState(false)
  const [searchData, setSearchData] = useState(null)
  const [prodResults, setProdResults] = useState([])
  const [searchType, setSearchType] = useState(initgend)
  const [currSort, setCurrSort] = useState('best')
  const [currSizes, setCurrSizes] = useState([])
  const [sizes, setSizes] = useState(null)
  const [suggests, setSuggests] = useState([])
  const [dontGuess, setDontGuess] = useState(false)
  const [searchTest, setSearchTest] = useState(false)
  const [tilestest, setTilestest] = useState(null)

  const searchApi = useSearchHook({searchType, currSort, currSizes, setSuggests, dontGuess})
  const { getSearchData, searchProds, filterSizes, getSearch, saveSearch } = searchApi

  const { closeListen } = useKeyboardEvent()

  const panelRef = useRef(null)
  const inptRef = useRef(null)
  const resultsRef = useRef(null)
  const searchTimeout = useRef(null)

  const handleClose = () => {
    const panel_el = panelRef.current

    if (!panel_el) return
    panel_el.setAttribute('aria-hidden', 'true')
    setTimeout(() => {
      setFreeze(false)
      setSearch(false)
    }, 200)
  }

  const searchClear = () => {
    setProdResults([])
    setCurrSort('best')
    setSizes(null)
    setCurrSizes([])
    setSuggests([])
    setDontGuess(false)
    localStorage.removeItem('search')
    inptRef.current.value = ''
  }

  const debounce = (func, delay) => {
    return (...args) => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current)
      }
      searchTimeout.current = setTimeout(() => {
        func(...args)
      }, delay)
    }
  }

  const handleSearch = (e=false) => {
    if (!searchData) return

    const val = inptRef.current.value.toLowerCase()

    if ( val === '' ) {
      searchClear()
      return
    }
    if (!!e && dontGuess) {
      setDontGuess(false)
      return
    }

    if ( val.length < 3 ) return

    let gendvals = compact(val.split(' '))
    const gends = ['men', 'mens', "men's", 'women', 'womens', "women's"]
    let has_gend = gendvals.find(x => gends.includes(x.toLowerCase()))
    let search_gend = has_gend?.toLowerCase()?.slice(0,1)
    if (has_gend && search_gend !== searchType) {
      setSearchType(search_gend)
      return
    }

    const prods = searchProds({val})
    if (!prods) return

    setProdResults(prods)
    if (localStorage.getItem('csearch')) parseSizes(prods)
    do_saving()
    const anaprods = prods.flat()
    debounce(elev_search, 2000)(val, anaprods.slice(0,36))
  }

  const parseSizes = (prods) => {
    let all_sizes = [...new Set( prods.flat().flatMap(product => Object.keys(product.variants)) )]
    all_sizes = orderSizes(all_sizes).filter(x => x.length > 0)

    let avail_sizes = prods.flat().flatMap(product => {
      return compact(Object.keys(product.variants).map(k => !!product.variants[k] ? k : null))
    })
    avail_sizes = [...new Set(compact(avail_sizes))]
    setSizes({all: all_sizes, avail: avail_sizes})
  }

  const orderSizes = (sizes) => {
    const size_order = ['xxs','2s','xs','s','m','l','xl','xxl','2x','xxxl','3x','os','one size']
    let str_sizes = sizes.filter(x => !Number.isInteger(Number(x)) && !x.includes('.'))
    str_sizes = str_sizes.sort((a, b) => size_order.indexOf(a.toLowerCase()) - size_order.indexOf(b.toLowerCase()) )

    let int_sizes = sizes.filter(x => Number.isInteger(Number(x)) || x.includes('.'))
    int_sizes = int_sizes.sort((a, b) => parseFloat(a) - parseFloat(b))

    let shoe_sizes = int_sizes.filter(x => x < 20)
    int_sizes = int_sizes.filter(x => !shoe_sizes.includes(x))
    return [str_sizes, int_sizes, shoe_sizes]
  }

  const gaClick = (product=false, isPopular=false, index=false) => {
    const val = inptRef.current.value

    const term = isPopular ? isPopular.toLowerCase() : inptRef.current.value
    const type = searchType === null ? 'all' : searchType === 'm' ? 'men' : searchType === 'w' ? 'women' : searchType

    ga_search_click(term, type, !!isPopular)
    if (product) {
      setAnalytics({...analytics, list: 'search results'})
      elev_select(product, index, 'search results')
    }
  }

  const get_search_data = async () => {
    const data = await getSearchData({})
    setSearchData(data)
  }

  const do_saving = async () => {
    const val = inptRef.current.value
    if ( val === '' ) return

    const obj = {
      prodResults: prodResults.flat(),
      searchType: searchType,
      currSort,
      suggests,
      dontGuess
    }

    const vals_arr = compact(val.split(' '))
    const has_saved = await saveSearch(obj, vals_arr)
    return has_saved
  }

  const goto_page = async () => {
    const has_saved = await do_saving()
    if (has_saved) {
      if (window.location.pathname.includes('/search')) {
        window.location = `/search`
        handleClose()
      } else {
        navigate('/search')
        handleClose()
      }
    }
  }

  const check_enter = (e) => {
    if (e.key !== 'Enter' || !searchData) return
    goto_page()
  }

  const checkSize = (e) => {
    if (!isMobile) {
      if (!!mobiHeight) setMobiHeight(null)
      return
    }
    const max = `${window.visualViewport.height}px`
    setMobiHeight(max)
  }

  const countText = () => {
    const num = prodResults.flat().length;
    return `${num} result${num !== 1 ? 's' : ''}`
  }

  const calloutText = (id) => {
    return searchData.callouts.find(x => x.id === id)?.data || {}
  }

  const tagMatch = (matches, tag) => {
    return matches.includes(tag) || matches.find(x => tag.includes(x))
  }

  const pubString = (stamp) => {
    const pub = new Date(stamp * 1000)
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

    return `${months[pub.getMonth()]} ${pub.getDate()} ${pub.getFullYear()}`
  }

  const toggleSize = (size) => {
    if (currSizes.includes(size)) {
      setCurrSizes(currSizes.filter(x => x !== size))
      return
    }
    setCurrSizes([...new Set([...currSizes, size])])
  }

  
  useEffect(() => {
    setBread({handle: 'search', title: 'Search Results'})
    if (localStorage.getItem('csearch')) setSearchTest(true)
    if (!isMobile) setTilestest(!!sessionStorage.getItem('tilestest'))

    setTimeout(() => {
      if (!isMobile && !tilestest) setTilestest(!!sessionStorage.getItem('tilestest'))
    }, 150)

    closeListen(handleClose)

    if (!searchData) get_search_data()
    if (!prodResults) localStorage.removeItem('search')
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!inptRef.current) return
    setTimeout(() => {
      if (inptRef.current) inptRef.current.focus()
    }, 100)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inptRef])

  useEffect(() => {
    if (!searchData) return
    resultsRef.current.scrollTo({
      top: 0,
      behavior: 'instant'
    })
    handleSearch()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchType, currSort, dontGuess])

  useEffect(() => {
    if (!searchData) return
    if (!sizes) return
    if (currSizes.length < 1 && !!sizes) {
      handleSearch()
      return
    }
    setProdResults( filterSizes({results: prodResults, val: inptRef.current.value}) )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currSizes])
  
  useEffect(() => {
    setFreeze(true)
    const panel = panelRef.current
    setTimeout(() => {
      panel.setAttribute('aria-hidden', 'false')
    }, 100)
  }, [setFreeze])


  useEffect(() => {
    window.addEventListener('keydown', check_enter)
    window.visualViewport.addEventListener('resize', checkSize)

    return () => {
      window.removeEventListener('keydown', check_enter)
      window.visualViewport.removeEventListener('resize', checkSize)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  })


  return (
    <section 
      className={styles.searchwr} 
      aria-label="Search products" 
      role="dialog" 
      aria-modal="true" 
      aria-hidden="true"
      ref={panelRef}
      style={{maxHeight: mobiHeight ? mobiHeight : ''}}
    >
      <button 
        type="button"
        className={[aria_btn, styles.close].join(' ')}
        aria-label="Close sidebar" 
        onClick={(e)=> {handleClose()}}
      >
        <IconClose icon_classes={''} />
      </button>
      <div className={styles.head} shead=''>
        <div className={styles.inputwr}>
          <input 
            type="search"
            name="q"
            placeholder="Search our store"
            className={styles.search_inpt}
            onChange={(e)=>{handleSearch(e)}}
            ref={inptRef}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck="false"
          />
          <div className={styles.search_btn}>
            <IconSearch icon_classes={''} />
          </div>
          <div className={styles.search_gender}>
            <button 
              type="button"
              className={`${aria_btn} ${styles.gender_btn}`} 
              aria-label="Search all products" 
              aria-current={searchType === null ? 'true' : 'false'}
              onClick={(e)=>{setSearchType(null)}}
            >
              All
            </button>
            <button 
              type="button"
              className={`${aria_btn} ${styles.gender_btn}`} 
              aria-label="Search mens products" 
              aria-current={searchType === 'm' ? 'true' : 'false'}
              onClick={(e)=>{setSearchType('m')}}
            >
              Men's
            </button>
            <button 
              type="button"
              className={`${aria_btn} ${styles.gender_btn}`} 
              aria-label="Search womens products" 
              aria-current={searchType === 'w' ? 'true' : 'false'}
              onClick={(e)=>{setSearchType('w')}}
            >
              Women's
            </button>
            <div className={styles.search_extra}>
              {inptRef?.current?.value != '' && (
                <button
                  type='button'
                  className={styles.clear}
                  onClick={(e) => searchClear()}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      {searchTest && suggests.length > 0 && (
        <div className={styles.suggestswr}>
          
          {(suggests && !dontGuess) ? (
            <>
              <div className={styles.suggests}>
                <div className={styles.suggestlab}>
                  Showing results for:
                </div>
                {suggests.map((term,index) =>
                  <div key={`${term}_${index}`} className={styles.suggest}>{term}</div>
                )}
              </div>
              <div className={styles.suggests}>
                <div className={styles.suggestlab}>
                  Search instead for:
                </div>
                <button
                  type='button'
                  className={styles.suggestbtn}
                  onClick={(e) => setDontGuess(true)}
                >
                  {inptRef.current.value}
                </button>
              </div>
            </>
          ) : (
            <div className={styles.suggests}>
              <div className={styles.suggestlab}>
                Search instead for:
              </div>
              <button
                type='button'
                className={styles.suggestbtn}
                onClick={(e) => setDontGuess(false)}
              >
                {suggests.join(' ')}
              </button>
            </div>
          )}
        </div>
      )}
      <div className={styles.resultswr} style={{maxHeight: (prodResults.length > 0) ? '90dvh' : 0}}>
        <div 
          className={styles.results} 
          ref={resultsRef}
        >
          {(prodResults.length > 0) && (
            <div className={styles.restitle}>
              <div className={styles.title}>{countText()}</div>
              <div className={styles.count_sorts}>
                <div className={styles.sorts}>
                  <button
                    type='button'
                    className={styles.sort}
                    aria-current={(currSort === 'best').toString()}
                    onClick={(e) => setCurrSort('best')}
                  >
                    Best Sellers
                  </button>
                  <button
                    type='button'
                    className={styles.sort}
                    aria-current={(currSort === 'rec').toString()}
                    onClick={(e) => setCurrSort('rec')}
                  >
                    Recent
                  </button>
                  <button
                    type='button'
                    className={styles.sort}
                    aria-current={(currSort === 'asc').toString()}
                    onClick={(e) => setCurrSort('asc')}
                  >
                    Price
                  </button>
                </div>
              </div>
              {(!!sizes && (prodResults.length > 1 || !!sizes)) && (
                <div className={styles.navsizeswr}>
                  {sizes.all.map((group, sindx) =>
                    <div key={`sizegrp_${sindx}`} className={styles.navsizes}>
                      {group.map(size =>
                        <button
                          key={`navsize_${size}`} 
                          type='button'
                          className={styles.navsize}
                          disabled={sizes.avail.includes(size) ? undefined : 'disabled'}
                          onClick={()=>toggleSize(size)}
                          aria-selected={(currSizes.includes(size)).toString()}
                        >
                          {size}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {prodResults.length > 0 && prodResults.map((group, index) => 
            <div key={`search_grp_${index}`} className={jnstyls([styles.group])}>
              {group.map((result, pindex) => {
                let image = result.media.length > 0 ? imgSize(result.media[0],300) : false

                return (
                  <Flink 
                    key={result.handle} 
                    href={`/products/${result.handle}`} 
                    className={jnstyls([styles.item])} 
                    result=""
                    onClick={(e)=>{
                      handleClose();
                      gaClick(result, false, index)
                    }}
                  >
                    {image && (
                      <div className={styles.image}>
                        <img src={image} alt={result.title} className={styles.img} loading="lazy" width="400" height="532" />
                      </div>
                    )}
                    <div className={styles.info} style={{rowGap: '0.4em'}}>
                      <div className={styles.item_title}>
                        <div className={styles.item_name}>
                          {(!searchType && searchType !== 'u') && (
                            <>
                              {result.gender === 'm' ? "Men's " : result.gender === 'w' ? "Women's " : ''}
                            </>
                          )}
                          {result.title}
                        </div>
                      </div>
                      <div className={styles.price}>
                        {result.handle.includes('gift') ? (
                          <span>
                            $25-100
                          </span>
                        ) : (
                          <>${result.price}</>
                        )}
                      </div>
                      <div className={jnstyls([styles.info, styles.infoextra])}>
                        <div className={styles.colors} style={{fontSize: '1rem'}}>
                          {(result?.pl_colors && (result?.pl_colors  - 1) > 0) && (
                            <>{result?.pl_colors  - 1} Other Color{(result?.pl_colors  - 1) > 1 ? 's' : ''}</>
                          )}
                        </div>
                        {(result?.altcuts) && (
                          <div className={styles.alts} style={{textTransform: 'capitalize'}}>
                            <div className={styles.altlabel}>
                              {result.altcuts.os.length} Length{result.altcuts.os.length !== 1 ? 's' : ''}
                            </div>
                            {result.altcuts.os.map(item =>
                              <div key={`prod_${result.mid}_alt_${item}`} className={styles.altitem}>
                                {item}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {!!sizes && (
                        <div className={styles.variants}>
                          {result.variants && Object.keys(result.variants).map(size => 
                            <div
                              key={`${result.mid}_var_${size}`} 
                              className={styles.variant}
                              disabled={!!result.variants[size] ? undefined : 'disabled'}
                            >
                              {size}
                            </div>
                          )}
                        </div>
                      )}
                      {(result.callouts?.length > 0) && (
                        <div className={jnstyls([styles.info, styles.infoextra])} style={{marginTop: '0.4em'}}>
                          <div className={styles.callouts} style={{textTransform: 'capitalize'}}>
                            {result.callouts.map(id =>
                              <div 
                                key={`prod_${result.mid}_call_${id}`}
                                className={styles.callout} 
                                style={{color: calloutText(id)?.color || ''}}
                              >
                                {calloutText(id).name}
                              </div>
                            )}
                          </div>
                          <div className={styles.alts}></div>
                        </div>
                      )}
                    </div>
                  </Flink>
                )
              })}
            </div>
          )}
        </div>
        <div className={styles.foot}>
          <button type="button" className={`${solid_btn} ${styles.btn}`} onClick={(e)=>{goto_page()}}> 
            See All Results
          </button>
        </div>
      </div>

      {tilestest !== null && (
        <>
          {tilestest ? (
            <SearchTiles searchType={searchType} val={inptRef?.current?.value} />
          ) : (
            <div className={styles.blind} style={{maxHeight: (prodResults.length > 0) ? '0' : ''}}>
              <div className={styles.popular}>
                {(!searchType || searchType === 'm') && content?.mens_links?.length > 0 && (
                  <div className={styles.pop_group}>
                    <div className={styles.title}>
                      Popular Men's Searches
                    </div>
                    <ol className={styles.popular_list}>
                      {content.mens_links.map(link => 
                        <li key={link.link_text} className={styles.popular_item}>
                          <Flink 
                            href={link.url} 
                            aria-label={`Popular Search: ${link.link_text}`}
                            onClick={(e)=>{
                              handleClose();
                              gaClick(false,link.link_text)
                            }} 
                          >
                            {link.link_text}
                          </Flink>
                        </li>
                      )}
                    </ol>
                  </div>
                )}
                {(!searchType || searchType === 'w') && content?.womens_links?.length > 0 && (
                  <div className={styles.pop_group}>
                    <div className={styles.title}>
                      Popular Women's Searches
                    </div>
                    <ol className={styles.popular_list}>
                      {content.womens_links.map(link => 
                        <li key={link.link_text} className={styles.popular_item}>
                          <Flink 
                            href={link.url} 
                            aria-label={`Popular Search: ${link.link_text}`}
                            onClick={(e)=>{
                              handleClose();
                              gaClick(false,link.link_text)
                            }} 
                          >
                            {link.link_text}
                          </Flink>
                        </li>
                      )}
                    </ol>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

    </section>
  )
}





export default Search
